/**
 * EventItem - Dispatches to the appropriate event renderer based on type
 */

import { CircleHelp } from "lucide-react"
import { observer } from "mobx-react"
import { useCodeStore } from "../store/context"
import type { ActionEvent, CodeEvent } from "../types"
import { FileViewer } from "./FilesAndDiffs"
import { ActionEventItem } from "./events/ActionEventItem"
import { SetupEventItem } from "./events/SetupEventItem"
import { SnapshotEventItem } from "./events/SnapshotEventItem"

/** Event types that start collapsed (their bodies lazy-load on expand); everything else starts expanded. */
const DEFAULT_COLLAPSED_TYPES: Set<CodeEvent["type"]> = new Set(["snapshot"])

export type EventRenderKind = "action" | "setup_environment" | "snapshot" | "unknown"

export function getEventRenderKind(event: { type?: unknown }): EventRenderKind {
    if (event.type === "action") return "action"
    // Legacy read-only rendering for pre-agent-owned-worktree task history.
    if (event.type === "setup_environment") return "setup_environment"
    if (event.type === "snapshot") return "snapshot"
    return "unknown"
}

function stringifyRaw(value: unknown): string {
    try {
        const json = JSON.stringify(value, null, 2)
        return json ?? String(value)
    } catch {
        return String(value)
    }
}

function UnknownEventItem({ event, expanded, onToggle }: { event: unknown; expanded: boolean; onToggle: () => void }) {
    const record = typeof event === "object" && event !== null && !Array.isArray(event) ? (event as Record<string, unknown>) : {}
    const type = typeof record.type === "string" && record.type.length > 0 ? record.type : "event"
    const createdAt = typeof record.createdAt === "string" ? record.createdAt : undefined
    const contents = stringifyRaw(event)

    return (
        <div className="border-b border-border bg-base-100">
            <button type="button" className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-base-200" onClick={onToggle}>
                <CircleHelp size="1em" className="flex-shrink-0 text-warning" />
                <span className="font-medium text-sm">Unknown event</span>
                <span className="text-sm text-muted truncate flex-1">{type}</span>
                {createdAt && <span className="text-xs text-muted flex-shrink-0">{createdAt}</span>}
            </button>
            {expanded && (
                <div className="px-3 pb-3">
                    <FileViewer
                        file={{ name: "unknown-task-event.json", contents, lang: "json" }}
                        copyContent={contents}
                        disableFileHeader
                        commentHandlers={null}
                    />
                </div>
            )}
        </div>
    )
}

export const EventItem = observer(({ taskId, event }: { taskId: string; event: CodeEvent }) => {
    const codeStore = useCodeStore()
    const renderKind = getEventRenderKind(event)

    const taskUIState = codeStore.tasks.getTaskUIState(taskId)
    const expanded = taskUIState.isEventExpanded(event.id, !DEFAULT_COLLAPSED_TYPES.has(event.type))

    const handleToggle = () => {
        taskUIState.toggleEventExpanded(event.id)
    }

    const baseProps = {
        expanded,
        onToggle: handleToggle,
    }

    switch (renderKind) {
        case "action": {
            const actionEvent = event as ActionEvent
            return <ActionEventItem {...baseProps} event={actionEvent} taskId={taskId} />
        }
        case "setup_environment":
            return <SetupEventItem {...baseProps} event={event as Extract<CodeEvent, { type: "setup_environment" }>} />
        case "snapshot":
            return <SnapshotEventItem {...baseProps} event={event as Extract<CodeEvent, { type: "snapshot" }>} taskId={taskId} />
        case "unknown":
            return <UnknownEventItem {...baseProps} event={event} />
        default:
            return null
    }
})
